import { type SVGProps } from "react";

/* aria — custom inline SVG icons (SF Symbols line style, 1.5px stroke)
 * Each icon is a React component accepting standard SVG props.
 * Color via `currentColor`, size via `width`/`height` (default 24).
 */

const baseProps = {
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

type IconProps = SVGProps<SVGSVGElement>;

const make =
  (paths: () => React.ReactNode) =>
  (props: IconProps) =>
    (
      <svg {...baseProps} {...props}>
        {paths()}
      </svg>
    );

export const PhoneIcon = make(() => (
  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
));

export const MessageIcon = make(() => (
  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
));

export const VideoIcon = make(() => (
  <>
    <rect x="2" y="6" width="14" height="12" rx="2.5" />
    <path d="M22 8.5 17 12l5 3.5v-7z" />
  </>
));

export const WhatsAppIcon = make(() => (
  <path d="M12 2a10 10 0 0 0-8.66 14.99L2 22l5.14-1.34A10 10 0 1 0 12 2zm0 18.13a8.13 8.13 0 0 1-4.14-1.13l-.3-.18-3.05.8.81-2.98-.19-.3A8.13 8.13 0 1 1 12 20.13zm4.55-5.97c-.25-.13-1.47-.73-1.69-.81-.23-.08-.39-.13-.56.13-.16.25-.64.81-.78.97-.15.16-.29.18-.54.06-.25-.13-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.13-.56-1.36-.77-1.86-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.08 0 1.23.9 2.42 1.02 2.59.13.17 1.77 2.71 4.29 3.8.6.26 1.07.42 1.43.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.1-.22-.16-.47-.29z" />
));

export const SlackIcon = make(() => (
  <>
    <path d="M5 9a3 3 0 1 1 3-3v3H5zm0 6a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm6-6h2v12h-2z" transform="translate(0 -1)" />
    <path d="M14 5a3 3 0 1 1 3 3h-3V5zm6 6a3 3 0 1 1-3 3v-3h3z" />
  </>
));

export const ContactsIcon = make(() => (
  <>
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <circle cx="12" cy="11" r="3" />
    <path d="M7 17c0-2.21 2.24-4 5-4s5 1.79 5 4" />
  </>
));

export const ClockIcon = make(() => (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </>
));

export const SettingsIcon = make(() => (
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </>
));

export const PlusIcon = make(() => (
  <>
    <path d="M12 5v14M5 12h14" />
  </>
));

export const SearchIcon = make(() => (
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </>
));

export const TrashIcon = make(() => (
  <>
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M10 11v6M14 11v6" />
  </>
));

export const ChevronRightIcon = make(() => <path d="m9 18 6-6-6-6" />);
export const ChevronLeftIcon = make(() => <path d="m15 18-6-6 6-6" />);
export const ChevronDownIcon = make(() => <path d="m6 9 6 6 6-6" />);

export const XIcon = make(() => (
  <>
    <path d="M18 6 6 18M6 6l12 12" />
  </>
));

export const CheckIcon = make(() => <path d="M20 6 9 17l-5-5" />);

export const PersonIcon = make(() => (
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
  </>
));

export const ShareIcon = make(() => (
  <>
    <path d="M12 3v12M8 7l4-4 4 4" />
    <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
  </>
));

export const SpeakerIcon = make(() => (
  <>
    <path d="M11 5 6 9H3v6h3l5 4V5z" />
    <path d="M16 9a3 3 0 0 1 0 6M19 6a7 7 0 0 1 0 12" />
  </>
));

export const SpeakerSlashIcon = make(() => (
  <>
    <path d="M11 5 6 9H3v6h3l5 4V5z" />
    <path d="m22 9-6 6M16 9l6 6" />
  </>
));

export const MicIcon = make(() => (
  <>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </>
));

export const MicSlashIcon = make(() => (
  <>
    <path d="M9 5a3 3 0 0 1 6 0v6M5 11a7 7 0 0 0 11 5M12 18v3" />
    <path d="m3 3 18 18" />
  </>
));

export const PhoneHangupIcon = make(() => (
  <path d="M22 9.5a18 18 0 0 0-20 0 2 2 0 0 0-.6 2.6l1.2 2.1a2 2 0 0 0 2.5.8l2.2-.9a2 2 0 0 0 1.2-1.8V10a14 14 0 0 1 6 0v1.4a2 2 0 0 0 1.2 1.8l2.2.9a2 2 0 0 0 2.5-.8l1.2-2.1A2 2 0 0 0 22 9.5z" transform="rotate(180 12 12)" />
));

export const PhoneTransferIcon = make(() => (
  <>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    <path d="M16 4h5v5M21 4l-7 7" />
  </>
));

export const RecordIcon = make(() => (
  <>
    <circle cx="12" cy="12" r="4" fill="currentColor" />
    <circle cx="12" cy="12" r="9" />
  </>
));

export const PlayIcon = make(() => <path d="m6 4 14 8-14 8V4z" fill="currentColor" />);
export const PauseIcon = make(() => (
  <>
    <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" />
    <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" />
  </>
));
export const StopIcon = make(() => <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />);

export const EllipsisIcon = make(() => (
  <>
    <circle cx="5" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="19" cy="12" r="1.5" fill="currentColor" />
  </>
));

export const InfoIcon = make(() => (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </>
));

export const AlertIcon = make(() => (
  <>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <path d="M12 9v4M12 17h.01" />
  </>
));

export const BellIcon = make(() => (
  <>
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </>
));

export const LockIcon = make(() => (
  <>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>
));

export const EnvelopeIcon = make(() => (
  <>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="m3 7 9 6 9-6" />
  </>
));

export const RefreshIcon = make(() => (
  <>
    <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
    <path d="M21 3v5h-5" />
  </>
));

export const StarIcon = make(() => (
  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
));

export const TagIcon = make(() => (
  <>
    <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <path d="M7 7h.01" />
  </>
));

export const AntennaIcon = make(() => (
  <>
    <path d="M5 7a7 7 0 0 1 14 0M8 9a4 4 0 0 1 8 0" />
    <path d="M12 11v9M9 20h6" />
  </>
));

export const WifiIcon = make(() => (
  <>
    <path d="M5 13a10 10 0 0 1 14 0" />
    <path d="M8.5 16.5a5 5 0 0 1 7 0" />
    <path d="M2 8.82a15 15 0 0 1 20 0" />
    <path d="M12 20h.01" />
  </>
));

export const WifiSlashIcon = make(() => (
  <>
    <path d="M5 13a10 10 0 0 1 14 0" />
    <path d="M8.5 16.5a5 5 0 0 1 7 0" />
    <path d="M2 8.82a15 15 0 0 1 20 0" />
    <path d="m2 2 20 20" />
  </>
));

export const MoonIcon = make(() => <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />);

export const SunIcon = make(() => (
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </>
));

export const QrCodeIcon = make(() => (
  <>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3M21 14v7h-7" />
  </>
));

export const GroupIcon = make(() => (
  <>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 19v-1a6 6 0 0 1 12 0v1" />
    <path d="M16 5.5a3 3 0 0 1 0 5M21 19v-1a6 6 0 0 0-4-5.5" />
  </>
));

export const PhoneAddIcon = make(() => (
  <>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    <path d="M19 4v6M16 7h6" />
  </>
));

export const ScreenShareIcon = make(() => (
  <>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
    <path d="M9 10l3-3 3 3M12 7v6" />
  </>
));

export const InstallIcon = make(() => (
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M12 8v7M9 13l3 3 3-3" />
  </>
));

export const NumberIcon = make(() => (
  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
));

export const ImageIcon = make(() => (
  <>
    <rect x="3" y="3" width="18" height="18" rx="2.5" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-5-5L5 21" />
  </>
));

export const SendIcon = make(() => (
  <>
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7z" />
  </>
));

export const LinkIcon = make(() => (
  <>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </>
));

export const ArrowDownLeftIcon = make(() => (
  <>
    <path d="M17 7 7 17M7 17v-6M7 17h6" />
  </>
));

export const ArrowUpRightIcon = make(() => (
  <>
    <path d="M7 17 17 7M17 7v6M17 7h-6" />
  </>
));

export const PhoneInIcon = make(() => (
  <>
    <path d="M15 2v6h6" />
    <path d="M15 2 21 8" />
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" transform="rotate(135 12 12)" />
  </>
));

export const PhoneOutIcon = make(() => (
  <>
    <path d="M15 2v6h6" />
    <path d="M15 2 21 8" />
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" transform="rotate(45 12 12)" />
  </>
));

export const CopyIcon = make(() => (
  <>
    <rect x="9" y="9" width="12" height="12" rx="2.5" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </>
));

export const LogoutIcon = make(() => (
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </>
));

export const EditIcon = make(() => (
  <>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </>
));

export const ShieldIcon = make(() => (
  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
));

export const UsersIcon = make(() => (
  <>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 19v-1a6 6 0 0 1 12 0v1" />
    <path d="M16 5.5a3 3 0 0 1 0 5M21 19v-1a6 6 0 0 0-4-5.5" />
  </>
));

export const HistoryIcon = make(() => (
  <>
    <path d="M3 3v5h5" />
    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
    <path d="M12 7v5l3 3" />
  </>
));

export const ChartIcon = make(() => (
  <>
    <path d="M3 3v16a2 2 0 0 0 2 2h16" />
    <path d="M8 17v-5M13 17V8M18 17v-3" />
  </>
));
export const HomeIcon = make(() => (
  <>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V21h14V9.5" />
    <path d="M9.5 21v-6h5v6" />
  </>
));

export const SnippetIcon = make(() => (
  <>
    <path d="M6 3h9l4 4v14H6z" />
    <path d="M15 3v4h4" />
    <path d="M9 12h6M9 16h4" />
  </>
));
